import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { apiFetch, getToken, STREAM_API } from '../utils/streamApi';
import MediaLightbox, { popupLink } from '../components/MediaLightbox';
import ConfirmDialog from '../components/ConfirmDialog';

// ── PACS access events — the badge that opened the door vs. the face the camera
//    recognised. Backend: /ui/pacs/* (see pacs_access.py).
//
// Four tabs, in the order the job is actually done:
//   Report    the join. Mismatches first, because that is the only reason to open
//             this page twice.
//   Events    the event table itself — typed, pasted, or POSTed by an integration.
//   Readers   which cameras watch which reader, and any user_id the attendance
//             employee directory does not already resolve.
//   Audit     who edited or deleted a row, and what it said before.
//
// Nothing here opens a door or fires a device: the module reads an event table and
// reports disagreement. A mismatch is a finding for a person to act on.

const box    = { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10, padding: 16, marginBottom: 14 };
const inp    = { padding: '8px 10px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--surface-alt)', color: 'var(--text)', fontSize: 13 };
const btn    = { padding: '8px 12px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--surface-alt)', color: 'var(--text)', fontSize: 13, cursor: 'pointer', fontWeight: 600 };
const btnPri = { ...btn, background: 'var(--primary)', borderColor: 'var(--primary)', color: '#fff' };
const btnDim = { ...btn, padding: '5px 9px', fontSize: 12, fontWeight: 500 };
const th     = { textAlign: 'left', padding: '8px 10px', fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.06em', whiteSpace: 'nowrap' };
const td     = { padding: '8px 10px', fontSize: 13, borderTop: '1px solid var(--border)', verticalAlign: 'top' };
const mono   = { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 12 };
const muted  = { color: 'var(--text-muted)' };

// Verdicts. `finding` drives the default filter and the "needs attention" tiles —
// the two config-gap verdicts are deliberately not findings: a reader nobody
// mapped is an installer's to-do, not a security event, and dressing it up as one
// is how a report gets ignored.
const VERDICTS = {
  IDENTITY_MISMATCH:   { label: 'Identity mismatch', fg: '#e07070', bg: 'rgba(224,112,112,0.14)', finding: true,
                         help: 'The badge says one person; the camera recognised a different known person.' },
  UNRECOGNISED_PERSON: { label: 'Unrecognised',      fg: '#e0b450', bg: 'rgba(224,180,80,0.15)',  finding: true,
                         help: 'Somebody was at the door and the camera recognised nobody — the badge holder is not enrolled, or it was not them.' },
  NO_CAMERA_EVIDENCE:  { label: 'No camera evidence', fg: '#9aa4b2', bg: 'rgba(154,164,178,0.14)', finding: true,
                         help: 'The badge opened the door and the camera saw nothing: blind spot, camera offline, or a remote/manual open.' },
  MATCH:               { label: 'Match',             fg: '#5cc98a', bg: 'rgba(92,201,138,0.15)',  finding: false,
                         help: 'The badge holder is who the camera recognised.' },
  USER_UNMAPPED:       { label: 'User not mapped',   fg: '#4da3ff', bg: 'rgba(77,163,255,0.14)',  finding: false,
                         help: 'This user_id resolves to no enrolled face. Map it under Readers & users, or give the employee an employee_id that matches.' },
  SYSTEM_UNMAPPED:     { label: 'Reader not mapped', fg: '#4da3ff', bg: 'rgba(77,163,255,0.14)',  finding: false,
                         help: 'No cameras are assigned to this reader, so there is nothing to compare against.' },
  EVIDENCE_EXPIRED:    { label: 'Evidence expired', fg: '#9aa4b2', bg: 'rgba(154,164,178,0.10)', finding: false,
                         help: 'The camera-side logs no longer reach back this far (they keep roughly the last 500 sightings), so nothing can be said about this badge read either way.' },
  UNBADGED_SIGHTING:   { label: 'No badge read',     fg: '#c98ae0', bg: 'rgba(201,138,224,0.15)', finding: true,
                         help: 'A known person was recognised at a badged door with no badge read at all — tailgating, a propped door, or a manual let-in.' },
};

// The three verdicts that are not judgements — no mapping, or no surviving
// evidence. One chip, so they never sit next to a real finding looking like one.
const UNJUDGED = 'USER_UNMAPPED,SYSTEM_UNMAPPED,EVIDENCE_EXPIRED';

const SOURCE_LABEL = {
  pacs_users:          'PACS user map',
  employee_directory:  'Employee directory',
  literal:             'user_id is the name',
};

const ymd  = d => new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
const fmt  = ts => { try { return new Date(ts * 1000).toLocaleString(); } catch { return '—'; } };
const hhmm = ts => { try { return new Date(ts * 1000).toLocaleTimeString(); } catch { return '—'; } };
// <input type="datetime-local"> wants local wall-clock with no zone; epoch → that.
const toLocalInput = ts => {
  if (!ts) return '';
  const d = new Date(ts * 1000);
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
};

function Pill({ verdict, children }) {
  const v = VERDICTS[verdict] || { label: verdict, fg: 'var(--text)', bg: 'var(--surface-alt)' };
  return (
    <span title={v.help || ''} style={{ background: v.bg, color: v.fg, borderRadius: 999,
      padding: '2px 9px', fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap' }}>
      {children || v.label}
    </span>
  );
}

function Tile({ verdict, n, active, onClick }) {
  const v = VERDICTS[verdict] || {};
  return (
    <button onClick={onClick} title={v.help || ''}
      style={{ ...box, marginBottom: 0, padding: '10px 14px', cursor: 'pointer', textAlign: 'left',
        minWidth: 128, borderColor: active ? v.fg : 'var(--border)',
        boxShadow: active ? `inset 0 0 0 1px ${v.fg}` : 'none' }}>
      <div style={{ fontSize: 22, fontWeight: 700, color: n ? v.fg : 'var(--text-muted)' }}>{n}</div>
      <div style={{ fontSize: 11, ...muted, textTransform: 'uppercase', letterSpacing: '.05em' }}>{v.label}</div>
    </button>
  );
}

// Compact camera multi-select. A reader watches one or two cameras out of a fleet
// that can run to hundreds, so this stays a closed summary until clicked and then
// gives a searchable checkbox list — the same reason AccessManagementPage has one.
function CameraPicker({ cameras, selected, onChange }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const onDoc = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);
  const byIdx = useMemo(() => new Map(cameras.map(c => [c.idx, c])), [cameras]);
  const shown = cameras.filter(c => !q || (c.name || '').toLowerCase().includes(q.toLowerCase()));
  const toggle = idx => onChange(selected.includes(idx) ? selected.filter(i => i !== idx) : [...selected, idx].sort((a, b) => a - b));
  return (
    <div ref={ref} style={{ position: 'relative', minWidth: 190 }}>
      <button onClick={() => setOpen(o => !o)} style={{ ...inp, cursor: 'pointer', width: '100%', textAlign: 'left' }}>
        {selected.length
          ? selected.map(i => byIdx.get(i)?.name || `Camera ${i}`).join(', ')
          : <span style={muted}>Pick cameras…</span>}
      </button>
      {open && (
        <div style={{ position: 'absolute', zIndex: 20, top: '100%', left: 0, marginTop: 4, width: 260,
          background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8, padding: 8,
          boxShadow: '0 8px 24px rgba(0,0,0,0.3)' }}>
          <input autoFocus value={q} onChange={e => setQ(e.target.value)} placeholder="Search cameras"
            style={{ ...inp, width: '100%', marginBottom: 6 }} />
          <div style={{ maxHeight: 220, overflowY: 'auto' }}>
            {shown.length === 0 && <div style={{ ...muted, fontSize: 12, padding: 6 }}>No match</div>}
            {shown.map(c => (
              <label key={c.idx} style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '5px 4px', fontSize: 13, cursor: 'pointer' }}>
                <input type="checkbox" checked={selected.includes(c.idx)} onChange={() => toggle(c.idx)} />
                {c.name || `Camera ${c.idx}`}
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function PacsAccessPage() {
  const [tab, setTab]       = useState('report');
  const [cfg, setCfg]       = useState(null);
  const [opts, setOpts]     = useState({ cameras: [], persons: [], employees: [], systems: [], users: [] });
  const [token, setToken]   = useState('');
  const [msg, setMsg]       = useState(null);
  const [media, setMedia]   = useState(null);

  useEffect(() => { getToken().then(t => setToken(t || '')); }, []);

  const loadCfg = useCallback(async () => {
    const [c, o] = await Promise.all([
      apiFetch('/ui/pacs/config').then(r => r.ok ? r.json() : null),
      apiFetch('/ui/pacs/form-options').then(r => r.ok ? r.json() : null),
    ]);
    if (c) setCfg(c);
    if (o) setOpts(o);
  }, []);
  useEffect(() => { loadCfg(); }, [loadCfg]);

  const flash = (type, text) => { setMsg({ type, text }); setTimeout(() => setMsg(null), 4500); };

  const saveSettings = async patch => {
    setCfg(c => ({ ...c, ...patch }));
    const r = await apiFetch('/ui/pacs/settings', { method: 'PUT', json: patch });
    if (!r.ok) { flash('error', 'Could not save the setting'); loadCfg(); return; }
    setCfg(await r.json());
  };

  if (!cfg) return <div style={{ padding: 20, ...muted }}>Loading…</div>;

  const TABS = [['report', 'Report'], ['events', 'Event table'],
                ['readers', 'Readers & users'], ['audit', 'Audit']];

  return (
    <div style={{ padding: 20, maxWidth: 1400 }}>
      <h1 style={{ fontSize: 22, margin: '0 0 4px' }}>Access Events</h1>
      <p style={{ ...muted, fontSize: 13, margin: '0 0 14px', maxWidth: 860 }}>
        The badge reader knows which card opened the door. The cameras know who was standing there.
        This page puts them side by side on the same door, at the same second, and reports where they
        disagree — a borrowed badge looks perfectly valid to the reader and perfectly normal to the camera,
        and only shows up in the comparison. Nothing here unlocks a door or raises an alert.
      </p>

      {msg && (
        <div style={{ ...box, padding: '10px 14px', borderColor: msg.type === 'error' ? '#e07070' : '#5cc98a',
          color: msg.type === 'error' ? '#e07070' : '#5cc98a', fontSize: 13 }}>{msg.text}</div>
      )}

      {/* Module switch + the window the whole report depends on. */}
      <div style={{ ...box, display: 'flex', flexWrap: 'wrap', gap: 18, alignItems: 'flex-end' }}>
        <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 14, fontWeight: 600 }}>
          <input type="checkbox" checked={!!cfg.enabled} onChange={e => saveSettings({ enabled: e.target.checked })} />
          Module enabled
        </label>
        <Field label="Match window before (s)" hint="A camera timestamp is when face confirmation FINISHED, not when the person appeared — keep this generous.">
          <input type="number" min={0} value={cfg.match_before_secs ?? 45} style={{ ...inp, width: 80 }}
            onChange={e => setCfg({ ...cfg, match_before_secs: e.target.value })}
            onBlur={e => saveSettings({ match_before_secs: Number(e.target.value) })} />
        </Field>
        <Field label="after (s)">
          <input type="number" min={0} value={cfg.match_after_secs ?? 30} style={{ ...inp, width: 80 }}
            onChange={e => setCfg({ ...cfg, match_after_secs: e.target.value })}
            onBlur={e => saveSettings({ match_after_secs: Number(e.target.value) })} />
        </Field>
        <Field label="Strong face score" hint="Labels a mismatch as strong or weak evidence. It never hides one.">
          <input type="number" step="0.01" min={0} max={1} value={cfg.strong_face_conf ?? 0.55} style={{ ...inp, width: 80 }}
            onChange={e => setCfg({ ...cfg, strong_face_conf: e.target.value })}
            onBlur={e => saveSettings({ strong_face_conf: Number(e.target.value) })} />
        </Field>
        <Field label="Keep events (days)">
          <input type="number" min={1} value={cfg.retention_days ?? 365} style={{ ...inp, width: 90 }}
            onChange={e => setCfg({ ...cfg, retention_days: e.target.value })}
            onBlur={e => saveSettings({ retention_days: Number(e.target.value) })} />
        </Field>
        <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13 }}
          title="Reports a known person recognised at a badged door with no badge read at all. Turn off if your cameras cover more than the badged doors.">
          <input type="checkbox" checked={cfg.report_unbadged !== false}
            onChange={e => saveSettings({ report_unbadged: e.target.checked })} />
          Report entries with no badge read
        </label>
      </div>

      {!cfg.enabled && (
        <div style={{ ...box, borderColor: '#e0b450', color: '#e0b450', fontSize: 13 }}>
          The module is off. The event table and the reader map can still be set up, and the report below
          runs on whatever is already stored — enabling it only starts the retention trim.
        </div>
      )}

      <div style={{ display: 'flex', gap: 6, marginBottom: 14, flexWrap: 'wrap' }}>
        {TABS.map(([k, label]) => (
          <button key={k} onClick={() => setTab(k)} style={tab === k ? btnPri : btn}>{label}</button>
        ))}
      </div>

      {tab === 'report'  && <ReportTab cfg={cfg} token={token} onMedia={setMedia} />}
      {tab === 'events'  && <EventsTab opts={opts} flash={flash} />}
      {tab === 'readers' && <ReadersTab cfg={cfg} opts={opts} flash={flash} reload={loadCfg} />}
      {tab === 'audit'   && <AuditTab />}

      <MediaLightbox media={media} onClose={() => setMedia(null)} />
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <div title={hint || ''}>
      <div style={{ fontSize: 11, ...muted, marginBottom: 4, textTransform: 'uppercase', letterSpacing: '.05em' }}>{label}</div>
      {children}
    </div>
  );
}


// ── Report ────────────────────────────────────────────────────────────────────

function ReportTab({ cfg, token, onMedia }) {
  const [from, setFrom]     = useState(ymd(new Date(Date.now() - 6 * 86400000)));
  const [to, setTo]         = useState(ymd(new Date()));
  const [filter, setFilter] = useState('FINDINGS');
  const [system, setSystem] = useState('');
  const [data, setData]     = useState(null);
  const [loading, setLoad]  = useState(false);
  const [openRow, setOpen]  = useState(null);

  const load = useCallback(async () => {
    setLoad(true);
    try {
      const q = new URLSearchParams({ date_from: `${from}T00:00:00`, date_to: `${to}T23:59:59` });
      if (filter) q.set('verdict', filter);
      if (system) q.set('system_id', system);
      const r = await apiFetch(`/ui/pacs/correlate?${q}`);
      setData(r.ok ? await r.json() : null);
    } finally { setLoad(false); }
  }, [from, to, filter, system]);
  useEffect(() => { load(); }, [load]);

  const counts = data?.counts || {};
  const rows   = data?.rows || [];
  const unbadged = data?.unbadged || [];
  const systems = cfg.systems || [];
  // Rows the report declines to judge, for the same reason in the end: there was
  // nothing to compare. Grouped into one chip so they don't compete with findings.
  const unjudged = (counts.USER_UNMAPPED || 0) + (counts.SYSTEM_UNMAPPED || 0) + (counts.EVIDENCE_EXPIRED || 0);
  // The camera logs are capped, so a range that starts before this is only
  // partly answerable — say so instead of letting the gap read as a finding.
  const floor = data?.window?.evidence_floor;
  const floorBites = floor && floor > (data?.window?.from || 0);

  const evUrl = ev => {
    if (!token) return null;
    if (ev.origin === 'known_seen' && ev.ref) return `${STREAM_API}/known-seen/${encodeURIComponent(ev.ref)}/snapshot?token=${token}`;
    if (ev.origin === 'attendance' && ev.image) return `${STREAM_API}/ui/attendance/evidence/${encodeURIComponent(ev.image)}?token=${token}`;
    if (ev.origin === 'alert' && ev.image) return `${STREAM_API}/alerts/${encodeURIComponent(ev.image)}/image?token=${token}`;
    return null;
  };
  const show = (ev, title) => { const u = evUrl(ev); if (u) onMedia({ kind: 'image', url: u, title }); };

  return (
    <>
      <div style={{ ...box, display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <Field label="From"><input type="date" value={from} onChange={e => setFrom(e.target.value)} style={inp} /></Field>
        <Field label="To"><input type="date" value={to} onChange={e => setTo(e.target.value)} style={inp} /></Field>
        <Field label="Reader">
          <select value={system} onChange={e => setSystem(e.target.value)} style={inp}>
            <option value="">All readers</option>
            {systems.map(s => <option key={s.system_id} value={s.system_id}>{s.name || s.system_id}</option>)}
          </select>
        </Field>
        <button onClick={load} style={btn} disabled={loading}>{loading ? 'Running…' : 'Refresh'}</button>
        {data?.truncated && <span style={{ ...muted, fontSize: 12 }}>Showing the newest rows only — narrow the dates.</span>}
      </div>

      {floorBites && (
        <div style={{ ...box, padding: '10px 14px', fontSize: 12, color: '#e0b450', borderColor: '#e0b450' }}>
          The camera-side logs only reach back to {fmt(floor)}. Badge reads before that are marked
          “evidence expired” rather than judged — the sightings they would have been compared against
          have already been trimmed.
        </div>
      )}

      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
        <Tile verdict="IDENTITY_MISMATCH"   n={counts.IDENTITY_MISMATCH || 0}   active={filter === 'IDENTITY_MISMATCH'}   onClick={() => setFilter('IDENTITY_MISMATCH')} />
        <Tile verdict="UNRECOGNISED_PERSON" n={counts.UNRECOGNISED_PERSON || 0} active={filter === 'UNRECOGNISED_PERSON'} onClick={() => setFilter('UNRECOGNISED_PERSON')} />
        <Tile verdict="UNBADGED_SIGHTING"   n={counts.UNBADGED_SIGHTING || 0}   active={filter === 'UNBADGED_SIGHTING'}   onClick={() => setFilter('UNBADGED_SIGHTING')} />
        <Tile verdict="NO_CAMERA_EVIDENCE"  n={counts.NO_CAMERA_EVIDENCE || 0}  active={filter === 'NO_CAMERA_EVIDENCE'}  onClick={() => setFilter('NO_CAMERA_EVIDENCE')} />
        <Tile verdict="MATCH"               n={counts.MATCH || 0}               active={filter === 'MATCH'}               onClick={() => setFilter('MATCH')} />
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap', alignItems: 'center' }}>
        <button onClick={() => setFilter('FINDINGS')} style={filter === 'FINDINGS' ? btnPri : btnDim}>Findings only</button>
        <button onClick={() => setFilter('')} style={filter === '' ? btnPri : btnDim}>Everything</button>
        {unjudged > 0 && (
          <button onClick={() => setFilter(UNJUDGED)} style={filter === UNJUDGED ? btnPri : btnDim}
            title="Not security findings — rows the report could not judge: a mapping is missing, or the camera logs no longer reach that far back.">
            {unjudged} unjudged
          </button>
        )}
        {counts.badge_user_conflict > 0 && (
          <span style={{ fontSize: 12, color: '#e07070' }}>
            {counts.badge_user_conflict} event{counts.badge_user_conflict === 1 ? '' : 's'} where the PACS itself
            lists the badge against a different user.
          </span>
        )}
        <span style={{ ...muted, fontSize: 12, marginLeft: 'auto' }}>
          {counts.total || 0} badge read{counts.total === 1 ? '' : 's'} in range · window −{data?.window?.before ?? 45}s/+{data?.window?.after ?? 30}s
        </span>
      </div>

      {(!filter || filter === 'FINDINGS' || filter === 'UNBADGED_SIGHTING') && (
        <UnbadgedTable rows={unbadged} onShow={show} />
      )}

      {/* The badge-read table is the whole point of the page, except when the
          filter asks only for entries that had NO badge read — then it would be
          an empty table under a full one. */}
      {filter !== 'UNBADGED_SIGHTING' && (
      <div style={{ ...box, padding: 0, overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 900 }}>
          <thead><tr>
            <th style={th}>When</th><th style={th}>Reader</th><th style={th}>Badge → user</th>
            <th style={th}>Badge says</th><th style={th}>Camera saw</th><th style={th}>Verdict</th><th style={th} />
          </tr></thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td style={{ ...td, ...muted }} colSpan={7}>
                {loading ? 'Running…' : 'Nothing in this range for that filter.'}
              </td></tr>
            )}
            {rows.map(r => {
              const open = openRow === r.id;
              return (
                <Fragment key={r.id}>
                  <tr>
                    <td style={{ ...td, whiteSpace: 'nowrap' }}>{fmt(r.ts)}</td>
                    <td style={td}>
                      {r.system_name || r.system_id}
                      {r.door && <div style={{ ...muted, fontSize: 11 }}>{r.door}</div>}
                    </td>
                    <td style={td}>
                      <div style={mono}>{r.badge_id} → {r.user_id}</div>
                      {r.badge_user_conflict && (
                        <div style={{ fontSize: 11, color: '#e07070' }}>
                          badge is registered to {r.badge_registered_to}
                        </div>
                      )}
                    </td>
                    <td style={td}>
                      {r.expected_person || <span style={muted}>unmapped</span>}
                      {r.expected_source && <div style={{ ...muted, fontSize: 11 }}>{SOURCE_LABEL[r.expected_source] || r.expected_source}</div>}
                    </td>
                    <td style={td}>
                      {r.seen_persons?.length
                        ? r.seen_persons.join(', ')
                        : <span style={muted}>{r.evidence?.length ? 'someone, recognised as nobody' : '—'}</span>}
                      {r.evidence_strength && (
                        <div style={{ ...muted, fontSize: 11 }}>
                          {r.evidence_strength} evidence{r.best_face_score ? ` · face ${r.best_face_score}` : ''}
                        </div>
                      )}
                    </td>
                    <td style={td}><Pill verdict={r.verdict} /></td>
                    <td style={td}>
                      {!!r.evidence?.length && (
                        <button onClick={() => setOpen(open ? null : r.id)} style={btnDim}>
                          {open ? 'Hide' : `${r.evidence.length} frame${r.evidence.length === 1 ? '' : 's'}`}
                        </button>
                      )}
                    </td>
                  </tr>
                  {open && (
                    <tr><td style={{ ...td, background: 'var(--surface-alt)' }} colSpan={7}>
                      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                        {r.evidence.map((ev, i) => (
                          <div key={i} style={{ fontSize: 12, minWidth: 150 }}>
                            <button {...popupLink({ onClick: () => show(ev, `${ev.name || 'Unrecognised'} · ${ev.cam_name} · ${hhmm(ev.ts)}`) })}
                              style={{ ...popupLink({}), color: 'var(--primary)', textDecoration: 'underline' }}>
                              {ev.name || 'Unrecognised person'}
                            </button>
                            <div style={muted}>{ev.cam_name || `Camera ${ev.cam_idx}`} · {hhmm(ev.ts)}</div>
                            <div style={muted}>
                              {ev.origin === 'alert' ? 'alert' : ev.origin === 'attendance' ? 'attendance punch' : 'known face'}
                              {ev.score ? ` · ${ev.score}` : ''}
                              {ev.authorized === false ? ' · out of hours' : ''}
                            </div>
                          </div>
                        ))}
                      </div>
                    </td></tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
      )}
    </>
  );
}

function UnbadgedTable({ rows, onShow }) {
  if (!rows.length) return null;
  return (
    <div style={{ ...box, padding: 0 }}>
      <div style={{ padding: '10px 14px', borderBottom: '1px solid var(--border)', fontSize: 13, fontWeight: 600 }}>
        Recognised at a badged door with no badge read <span style={{ ...muted, fontWeight: 400 }}>({rows.length})</span>
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 640 }}>
          <thead><tr>
            <th style={th}>When</th><th style={th}>Person</th><th style={th}>Camera</th>
            <th style={th}>Reader</th><th style={th}>Verdict</th>
          </tr></thead>
          <tbody>
            {rows.map((u, i) => (
              <tr key={`${u.ref}-${i}`}>
                <td style={{ ...td, whiteSpace: 'nowrap' }}>{fmt(u.ts)}</td>
                <td style={td}>
                  <button {...popupLink({ onClick: () => onShow(u, `${u.person} · ${u.cam_name} · ${hhmm(u.ts)}`) })}
                    style={{ ...popupLink({}), color: 'var(--primary)', textDecoration: 'underline' }}>
                    {u.person}
                  </button>
                  {u.authorized === false && <div style={{ fontSize: 11, color: '#e0b450' }}>out of hours</div>}
                </td>
                <td style={td}>{u.cam_name || `Camera ${u.cam_idx}`}</td>
                <td style={td}>{(u.systems || []).join(', ')}</td>
                <td style={td}><Pill verdict="UNBADGED_SIGHTING" /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}


// ── Event table ───────────────────────────────────────────────────────────────

const blankEvent = () => ({ system_id: '', event_id: '', badge_id: '', user_id: '', ts: '', door: '', direction: '', result: '' });

function EventsTab({ opts, flash }) {
  const [rows, setRows]   = useState([]);
  const [from, setFrom]   = useState('');
  const [to, setTo]       = useState('');
  const [draft, setDraft] = useState(null);     // new row
  const [edit, setEdit]   = useState(null);     // { id, ...fields }
  const [sel, setSel]     = useState([]);
  const [confirm, setConfirm] = useState(null);
  const [paste, setPaste] = useState(null);     // import text, or null when closed
  const [busy, setBusy]   = useState(false);

  const load = useCallback(async () => {
    const q = new URLSearchParams({ limit: '1000' });
    if (from) q.set('date_from', `${from}T00:00:00`);
    if (to)   q.set('date_to', `${to}T23:59:59`);
    const r = await apiFetch(`/ui/pacs/events?${q}`);
    if (r.ok) { setRows((await r.json()).events || []); setSel([]); }
  }, [from, to]);
  useEffect(() => { load(); }, [load]);

  const systems = opts.systems || [];

  const save = async () => {
    setBusy(true);
    try {
      const body = { ...draft };
      const r = await apiFetch('/ui/pacs/events', { method: 'POST', json: body });
      if (!r.ok) { flash('error', (await r.json().catch(() => ({}))).detail || 'Could not add the event'); return; }
      setDraft(null); flash('ok', 'Event added'); load();
    } finally { setBusy(false); }
  };

  const update = async () => {
    setBusy(true);
    try {
      const { id, ...patch } = edit;
      const r = await apiFetch(`/ui/pacs/events/${encodeURIComponent(id)}`, { method: 'PUT', json: patch });
      if (!r.ok) { flash('error', (await r.json().catch(() => ({}))).detail || 'Could not save the event'); return; }
      setEdit(null); flash('ok', 'Event updated'); load();
    } finally { setBusy(false); }
  };

  const doDelete = async ids => {
    const r = await apiFetch('/ui/pacs/events/delete', { method: 'POST', json: { event_ids: ids } });
    if (!r.ok) { flash('error', 'Could not delete'); return; }
    const { deleted } = await r.json();
    flash('ok', `${deleted} event${deleted === 1 ? '' : 's'} deleted — recorded in Audit`);
    load();
  };

  const doImport = async () => {
    setBusy(true);
    try {
      const r = await apiFetch('/ui/pacs/events/import', { method: 'POST', json: { text: paste } });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) { flash('error', j.detail || 'Import failed'); return; }
      const bits = [`${j.added} added`];
      if (j.duplicates) bits.push(`${j.duplicates} already recorded`);
      if (j.rejected)   bits.push(`${j.rejected} rejected`);
      flash(j.added ? 'ok' : 'error', bits.join(' · ') + (j.errors?.length ? ` — first problem: row ${j.errors[0].row}, ${j.errors[0].reason}` : ''));
      if (j.added) { setPaste(null); load(); }
    } finally { setBusy(false); }
  };

  const fields = [
    ['system_id', 'System id', 140], ['event_id', 'Event id', 120],
    ['badge_id', 'Badge id', 120], ['user_id', 'User id', 120],
  ];

  return (
    <>
      <div style={{ ...box, display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <Field label="From"><input type="date" value={from} onChange={e => setFrom(e.target.value)} style={inp} /></Field>
        <Field label="To"><input type="date" value={to} onChange={e => setTo(e.target.value)} style={inp} /></Field>
        <button onClick={load} style={btn}>Refresh</button>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          <button onClick={() => { setDraft(blankEvent()); setEdit(null); }} style={btnPri}>+ Add event</button>
          <button onClick={() => setPaste(paste === null ? '' : null)} style={btn}>{paste === null ? 'Import / paste' : 'Close import'}</button>
          {sel.length > 0 && (
            <button style={{ ...btn, borderColor: '#e07070', color: '#e07070' }}
              onClick={() => setConfirm({
                title: `Delete ${sel.length} event${sel.length === 1 ? '' : 's'}?`,
                message: 'The rows go from the table. What they said, and who removed them, stays in Audit.',
                onYes: () => doDelete(sel),
              })}>Delete {sel.length}</button>
          )}
        </div>
      </div>

      {paste !== null && (
        <div style={box}>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Paste an export</div>
          <p style={{ ...muted, fontSize: 12, margin: '0 0 8px', maxWidth: 820 }}>
            CSV, TSV or JSON. The header row is matched case-insensitively against the usual names
            (<span style={mono}>controller / panel</span>, <span style={mono}>transaction id</span>,
            <span style={mono}> card number</span>, <span style={mono}>person id</span>,
            <span style={mono}> event time</span>), so an export does not need renaming first.
            Rows the PACS already gave us — same system id and event id — are skipped, so re-importing
            yesterday's file adds nothing.
          </p>
          <textarea value={paste} onChange={e => setPaste(e.target.value)} rows={8} spellCheck={false}
            placeholder={'system_id,event_id,badge_id,user_id,ts\nGATE-1,80114,0042918,1042,2026-10-11 09:14:03'}
            style={{ ...inp, ...mono, width: '100%', resize: 'vertical' }} />
          <div style={{ marginTop: 8 }}>
            <button onClick={doImport} style={btnPri} disabled={busy || !paste.trim()}>{busy ? 'Importing…' : 'Import'}</button>
          </div>
        </div>
      )}

      {draft && (
        <div style={box}>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 10 }}>New access event</div>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <Field label="System id">
              <input list="pacs-systems" value={draft.system_id} style={{ ...inp, width: 150 }}
                onChange={e => setDraft({ ...draft, system_id: e.target.value })} />
              <datalist id="pacs-systems">
                {systems.map(s => <option key={s.system_id} value={s.system_id}>{s.name}</option>)}
              </datalist>
            </Field>
            <Field label="Event id"><input value={draft.event_id} style={{ ...inp, width: 120 }}
              onChange={e => setDraft({ ...draft, event_id: e.target.value })} /></Field>
            <Field label="Badge id"><input value={draft.badge_id} style={{ ...inp, width: 130 }}
              onChange={e => setDraft({ ...draft, badge_id: e.target.value })} /></Field>
            <Field label="User id" hint="Matched against the PACS user map, then an employee_id in the employee directory.">
              <input list="pacs-users" value={draft.user_id} style={{ ...inp, width: 130 }}
                onChange={e => setDraft({ ...draft, user_id: e.target.value })} />
              <datalist id="pacs-users">
                {(opts.users || []).map(u => <option key={u.user_id} value={u.user_id}>{u.name}</option>)}
                {(opts.employees || []).filter(e => e.employee_id)
                  .map(e => <option key={`emp-${e.employee_id}`} value={e.employee_id}>{e.name}</option>)}
              </datalist>
            </Field>
            <Field label="Timestamp"><input type="datetime-local" step="1" value={draft.ts} style={inp}
              onChange={e => setDraft({ ...draft, ts: e.target.value })} /></Field>
            <Field label="Door (optional)"><input value={draft.door} style={{ ...inp, width: 130 }}
              onChange={e => setDraft({ ...draft, door: e.target.value })} /></Field>
            <Field label="Direction">
              <select value={draft.direction} onChange={e => setDraft({ ...draft, direction: e.target.value })} style={inp}>
                <option value="">—</option><option value="in">In</option><option value="out">Out</option>
              </select>
            </Field>
            <Field label="PACS result"><input value={draft.result} placeholder="granted" style={{ ...inp, width: 110 }}
              onChange={e => setDraft({ ...draft, result: e.target.value })} /></Field>
            <button onClick={save} style={btnPri} disabled={busy}>Add</button>
            <button onClick={() => setDraft(null)} style={btn}>Cancel</button>
          </div>
        </div>
      )}

      <div style={{ ...box, padding: 0, overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 980 }}>
          <thead><tr>
            <th style={{ ...th, width: 28 }}>
              <input type="checkbox" checked={sel.length > 0 && sel.length === rows.length}
                onChange={e => setSel(e.target.checked ? rows.map(r => r.id) : [])} />
            </th>
            <th style={th}>When</th>
            {fields.map(([k, label]) => <th key={k} style={th}>{label}</th>)}
            <th style={th}>Door</th><th style={th}>Dir</th><th style={th}>Result</th>
            <th style={th}>Added</th><th style={th} />
          </tr></thead>
          <tbody>
            {rows.length === 0 && <tr><td style={{ ...td, ...muted }} colSpan={11}>No access events stored for this range.</td></tr>}
            {rows.map(r => edit?.id === r.id ? (
              <tr key={r.id}>
                <td style={td} />
                <td style={td}><input type="datetime-local" step="1" value={edit.ts} style={{ ...inp, width: 190 }}
                  onChange={e => setEdit({ ...edit, ts: e.target.value })} /></td>
                {fields.map(([k, , w]) => (
                  <td key={k} style={td}>
                    <input value={edit[k] ?? ''} style={{ ...inp, width: w - 20 }}
                      onChange={e => setEdit({ ...edit, [k]: e.target.value })} />
                  </td>
                ))}
                <td style={td}><input value={edit.door ?? ''} style={{ ...inp, width: 110 }}
                  onChange={e => setEdit({ ...edit, door: e.target.value })} /></td>
                <td style={td}>
                  <select value={edit.direction ?? ''} onChange={e => setEdit({ ...edit, direction: e.target.value })} style={{ ...inp, width: 80 }}>
                    <option value="">—</option><option value="in">In</option><option value="out">Out</option>
                  </select>
                </td>
                <td style={td}><input value={edit.result ?? ''} style={{ ...inp, width: 90 }}
                  onChange={e => setEdit({ ...edit, result: e.target.value })} /></td>
                <td style={td} />
                <td style={{ ...td, whiteSpace: 'nowrap' }}>
                  <button onClick={update} style={{ ...btnPri, padding: '5px 9px', fontSize: 12 }} disabled={busy}>Save</button>{' '}
                  <button onClick={() => setEdit(null)} style={btnDim}>Cancel</button>
                </td>
              </tr>
            ) : (
              <tr key={r.id}>
                <td style={td}>
                  <input type="checkbox" checked={sel.includes(r.id)}
                    onChange={e => setSel(e.target.checked ? [...sel, r.id] : sel.filter(i => i !== r.id))} />
                </td>
                <td style={{ ...td, whiteSpace: 'nowrap' }}>{fmt(r.ts)}</td>
                <td style={{ ...td, ...mono }}>{r.system_id}</td>
                <td style={{ ...td, ...mono }}>{r.event_id}</td>
                <td style={{ ...td, ...mono }}>{r.badge_id}</td>
                <td style={{ ...td, ...mono }}>{r.user_id}</td>
                <td style={td}>{r.door || <span style={muted}>—</span>}</td>
                <td style={td}>{r.direction || <span style={muted}>—</span>}</td>
                <td style={td}>{r.result || <span style={muted}>—</span>}</td>
                <td style={{ ...td, ...muted, fontSize: 11 }}>
                  {r.source}{r.edited_ts ? ' · edited' : ''}
                </td>
                <td style={{ ...td, whiteSpace: 'nowrap' }}>
                  <button style={btnDim} onClick={() => { setEdit({ ...r, ts: toLocalInput(r.ts) }); setDraft(null); }}>Edit</button>{' '}
                  <button style={{ ...btnDim, color: '#e07070' }}
                    onClick={() => setConfirm({
                      title: 'Delete this access event?',
                      message: `${r.system_id} / ${r.event_id} — badge ${r.badge_id}, user ${r.user_id}. What it said stays in Audit.`,
                      onYes: () => doDelete([r.id]),
                    })}>Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {confirm && (
        <ConfirmDialog title={confirm.title} message={confirm.message} confirmLabel="Delete" confirmClass="danger"
          onConfirm={() => { confirm.onYes(); setConfirm(null); }} onCancel={() => setConfirm(null)} />
      )}
    </>
  );
}


// ── Readers & users ───────────────────────────────────────────────────────────

function ReadersTab({ cfg, opts, flash, reload }) {
  const [systems, setSystems] = useState(cfg.systems || []);
  const [users, setUsers]     = useState(cfg.users || []);
  const [busy, setBusy]       = useState(false);
  useEffect(() => { setSystems(cfg.systems || []); setUsers(cfg.users || []); }, [cfg]);

  const saveSystems = async rows => {
    setBusy(true);
    try {
      const r = await apiFetch('/ui/pacs/systems', { method: 'PUT', json: { systems: rows } });
      if (!r.ok) { flash('error', 'Could not save the readers'); return; }
      setSystems((await r.json()).systems || []);
      flash('ok', 'Readers saved'); reload();
    } finally { setBusy(false); }
  };
  const saveUsers = async rows => {
    setBusy(true);
    try {
      const r = await apiFetch('/ui/pacs/users', { method: 'PUT', json: { users: rows } });
      if (!r.ok) { flash('error', 'Could not save the user map'); return; }
      setUsers((await r.json()).users || []);
      flash('ok', 'User map saved'); reload();
    } finally { setBusy(false); }
  };

  const resolvedByDirectory = (opts.employees || []).filter(e => e.employee_id);

  return (
    <>
      <div style={box}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 6 }}>
          <h2 style={{ fontSize: 16, margin: 0 }}>Readers</h2>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
            <button style={btn} onClick={() => setSystems([...systems, { system_id: '', name: '', door: '', cameras: [], active: true }])}>+ Add reader</button>
            <button style={btnPri} onClick={() => saveSystems(systems)} disabled={busy}>Save readers</button>
          </div>
        </div>
        <p style={{ ...muted, fontSize: 12, margin: '0 0 10px', maxWidth: 820 }}>
          The <span style={mono}>system id</span> is whatever the PACS puts in its export for this reader or
          controller — it has to match character for character, or its events have nothing to compare against.
          Assign the cameras that actually see the person standing at it; a camera pointed at the corridor
          behind the door will read as "no camera evidence" all day.
        </p>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 760 }}>
            <thead><tr>
              <th style={th}>System id</th><th style={th}>Name</th><th style={th}>Door</th>
              <th style={th}>Cameras</th><th style={th}>Active</th><th style={th} />
            </tr></thead>
            <tbody>
              {systems.length === 0 && <tr><td style={{ ...td, ...muted }} colSpan={6}>No readers yet — add one and the report can start judging events.</td></tr>}
              {systems.map((s, i) => {
                const set = patch => setSystems(systems.map((x, j) => j === i ? { ...x, ...patch } : x));
                return (
                  <tr key={i}>
                    <td style={td}><input value={s.system_id || ''} placeholder="GATE-1" style={{ ...inp, ...mono, width: 130 }}
                      onChange={e => set({ system_id: e.target.value })} /></td>
                    <td style={td}><input value={s.name || ''} placeholder="Main gate reader" style={{ ...inp, width: 170 }}
                      onChange={e => set({ name: e.target.value })} /></td>
                    <td style={td}><input value={s.door || ''} placeholder="Lobby door" style={{ ...inp, width: 140 }}
                      onChange={e => set({ door: e.target.value })} /></td>
                    <td style={td}><CameraPicker cameras={opts.cameras || []} selected={s.cameras || []}
                      onChange={cams => set({ cameras: cams })} /></td>
                    <td style={td}><input type="checkbox" checked={s.active !== false}
                      onChange={e => set({ active: e.target.checked })} /></td>
                    <td style={td}>
                      <button style={{ ...btnDim, color: '#e07070' }}
                        onClick={() => setSystems(systems.filter((_, j) => j !== i))}>Remove</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div style={box}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 6 }}>
          <h2 style={{ fontSize: 16, margin: 0 }}>User map <span style={{ ...muted, fontWeight: 400, fontSize: 13 }}>(only for the gaps)</span></h2>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
            <button style={btn} onClick={() => setUsers([...users, { user_id: '', name: '', badge_id: '', department: '' }])}>+ Add user</button>
            <button style={btnPri} onClick={() => saveUsers(users)} disabled={busy}>Save user map</button>
          </div>
        </div>
        <p style={{ ...muted, fontSize: 12, margin: '0 0 10px', maxWidth: 820 }}>
          A PACS <span style={mono}>user_id</span> already resolves on its own when it matches an
          employee id in the employee directory
          {resolvedByDirectory.length ? ` (${resolvedByDirectory.length} employee${resolvedByDirectory.length === 1 ? '' : 's'} there have one)` : ''},
          or when it is literally the known-face name. Add a row here only for the ids that do not — and
          filling in the badge id lets the report flag a badge the PACS itself lists against someone else.
        </p>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 700 }}>
            <thead><tr>
              <th style={th}>User id</th><th style={th}>Known face</th><th style={th}>Badge id</th>
              <th style={th}>Department</th><th style={th} />
            </tr></thead>
            <tbody>
              {users.length === 0 && <tr><td style={{ ...td, ...muted }} colSpan={5}>Nothing mapped here — ids resolve through the employee directory.</td></tr>}
              {users.map((u, i) => {
                const set = patch => setUsers(users.map((x, j) => j === i ? { ...x, ...patch } : x));
                const known = (opts.persons || []).includes(u.name);
                return (
                  <tr key={i}>
                    <td style={td}><input value={u.user_id || ''} style={{ ...inp, ...mono, width: 110 }}
                      onChange={e => set({ user_id: e.target.value })} /></td>
                    <td style={td}>
                      <input list="pacs-persons" value={u.name || ''} style={{ ...inp, width: 180 }}
                        onChange={e => set({ name: e.target.value })} />
                      <datalist id="pacs-persons">
                        {(opts.persons || []).map(p => <option key={p} value={p} />)}
                      </datalist>
                      {u.name && !known && (
                        <div style={{ fontSize: 11, color: '#e0b450' }}>not an enrolled face — this id will never match</div>
                      )}
                    </td>
                    <td style={td}><input value={u.badge_id || ''} style={{ ...inp, ...mono, width: 120 }}
                      onChange={e => set({ badge_id: e.target.value })} /></td>
                    <td style={td}><input value={u.department || ''} style={{ ...inp, width: 130 }}
                      onChange={e => set({ department: e.target.value })} /></td>
                    <td style={td}>
                      <button style={{ ...btnDim, color: '#e07070' }}
                        onClick={() => setUsers(users.filter((_, j) => j !== i))}>Remove</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}


// ── Audit ─────────────────────────────────────────────────────────────────────

function AuditTab() {
  const [rows, setRows] = useState([]);
  useEffect(() => {
    apiFetch('/ui/pacs/audit?limit=500').then(r => r.ok ? r.json() : { audit: [] })
      .then(j => setRows(j.audit || []));
  }, []);
  const describe = r => {
    if (r.action === 'import') return `${r.after?.added ?? 0} added, ${r.after?.duplicates ?? 0} duplicates, ${r.after?.rejected ?? 0} rejected (${r.after?.source || 'import'})`;
    const s = r.before || r.after || {};
    return `${s.system_id || '—'} / ${s.event_id || '—'} · badge ${s.badge_id || '—'} · user ${s.user_id || '—'}${s.ts ? ` · ${fmt(s.ts)}` : ''}`;
  };
  return (
    <div style={{ ...box, padding: 0, overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 760 }}>
        <thead><tr>
          <th style={th}>When</th><th style={th}>Action</th><th style={th}>Who</th>
          <th style={th}>Event</th><th style={th}>Reason</th>
        </tr></thead>
        <tbody>
          {rows.length === 0 && <tr><td style={{ ...td, ...muted }} colSpan={5}>Nothing recorded yet.</td></tr>}
          {rows.map((r, i) => (
            <tr key={i}>
              <td style={{ ...td, whiteSpace: 'nowrap' }}>{fmt(r.ts)}</td>
              <td style={td}>{r.action}</td>
              <td style={td}>{r.actor || <span style={muted}>—</span>}</td>
              <td style={{ ...td, fontSize: 12 }}>{describe(r)}</td>
              <td style={{ ...td, ...muted, fontSize: 12 }}>{r.reason || '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
